import { RequestDraftRoute, RequestDraftStatus } from '@prisma/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useDebounce } from '../useDebounce';

export type DraftListRow = TrpcOutputs['getRequestDrafts']['drafts'][number];

/** Pull the next page in three rows early, so the list has already grown by the time the reviewer reaches its end. */
const ROWS_BEFORE_END = 3;

type DraftList = {
  rows: DraftListRow[];
  query: string;
  setQuery: (query: string) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  /** The row whose appearance asks for the next page. */
  triggerIndex: number;
  loadMore: () => void;
  refetch: () => Promise<unknown>;
};

/**
 * The AI Inbox queue: one tab's drafts, filtered by a search the reviewer types, loaded a page at a time as they
 * scroll. Pages come from React Query so that refetching after a draft is discarded or deleted refreshes every page
 * already on screen, not just the first.
 */
export const useDraftList = (status: RequestDraftStatus, route: RequestDraftRoute): DraftList => {
  const [query, setQuery] = useState('');
  // Typing is not a search; searching is what typing settles into.
  const search = useDebounce(query, 300);

  const { data, isError, isFetching, isFetchingNextPage, isPreviousData, hasNextPage, fetchNextPage, refetch } =
    trpc.getRequestDrafts.useInfiniteQuery(
      { status, route, search },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        // A narrowing search keeps the previous rows up while the next ones load, instead of blanking the queue
        // between keystrokes.
        keepPreviousData: true,
      },
    );

  // The tab the rows on screen were fetched for, recorded each time a fetch of the current query settles.
  const fetchedStatusRef = useRef(status);
  useEffect(() => {
    if (data && !isPreviousData) fetchedStatusRef.current = status;
  }, [data, isPreviousData, status]);

  const fetchedRows = useMemo(() => (data?.pages ?? []).flatMap((page) => page.drafts), [data]);
  // `keepPreviousData` holds the rows on screen while a search settles. Across a TAB change those rows belong to the
  // other queue — the screen would select the first of them, or call this tab empty because that one was — so until
  // the new tab arrives they count as not loaded.
  const isPreviousTab = isPreviousData && fetchedStatusRef.current !== status;
  const rows = isPreviousTab ? [] : fetchedRows;

  const loadMore = useCallback(() => {
    // A page that failed is not asked for again from here: the trigger row is still in view, so every settled failure
    // would start the next attempt, forever. The next refetch, search or tab clears the error.
    if (!hasNextPage || isFetchingNextPage || isError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return {
    rows,
    query,
    setQuery,
    isLoading: (!data && isFetching) || isPreviousTab,
    isLoadingMore: isFetchingNextPage,
    // Kept in range so a list shorter than the lead-in still has a trigger.
    triggerIndex: Math.max(rows.length - ROWS_BEFORE_END, 0),
    loadMore,
    refetch,
  };
};
